import * as React from 'react';
import Chart from 'chart.js/auto';

type Timeframe = '1D' | '5D' | '1M' | '6M' | 'YTD' | '1Y' | '5Y' | '10Y' | 'ALL';
type Basis = 'price' | 'total_return';
type Variant = 'inline' | 'full';

interface SeriesPoint {
  t: string;
  close: number | null;
  adj_close: number | null;
}

interface Summary {
  start_price: number | null;
  end_price: number | null;
  change: number | null;
  change_percent: number | null;
}

interface LatestQuote {
  price: number | null;
  change: number | null;
  change_percent: number | null;
}

interface PerformanceResponse {
  ticker: string;
  timeframe: Timeframe;
  basis: Basis;
  series: SeriesPoint[];
  summary: Summary;
  latest_quote: LatestQuote;
}

interface Props {
  ticker: string;
  variant?: Variant;
  fullPageHref?: string;
}

const TIMEFRAMES: Timeframe[] = ['1D', '5D', '1M', '6M', 'YTD', '1Y', '5Y', '10Y', 'ALL'];

const TIMEFRAME_COPY: Record<Timeframe, string> = {
  '1D': 'today',
  '5D': 'over the past 5 days',
  '1M': 'over the past month',
  '6M': 'over the past 6 months',
  'YTD': 'year to date',
  '1Y': 'over the past year',
  '5Y': 'over the past 5 years',
  '10Y': 'over the past 10 years',
  'ALL': 'over its full history'
};

function formatCurrency(value: number | null | undefined): string {
  if (value === null || value === undefined || Number.isNaN(value)) return '\u2014';
  return `$${value.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
}

function formatPercent(value: number | null | undefined): string {
  if (value === null || value === undefined || Number.isNaN(value)) return '\u2014';
  const sign = value > 0 ? '+' : '';
  return `${sign}${value.toFixed(2)}%`;
}

// FMP returns two shapes of timestamp:
//   * daily EOD:  "YYYY-MM-DD"              (used for 5D/1M/6M/YTD/1Y/5Y/10Y/ALL)
//   * intraday:   "YYYY-MM-DD HH:mm:ss"     (used for 1D)
// Per the ECMAScript spec, `new Date('YYYY-MM-DD')` is parsed as UTC midnight,
// which in negative-offset time zones (e.g. all of the continental US) renders
// as the PREVIOUS calendar day once we format it back to local time -- so
// daily x-axis ticks and tooltip titles would silently be off by one day.
// To avoid that, we detect the date-only shape and build the Date from its
// Y/M/D parts at LOCAL midnight. The date+time shape is already parsed as
// local time by `new Date('YYYY-MM-DDTHH:mm:ss')` and needs no special case.
function formatDateLabel(iso: string, compact: boolean): string {
  if (!iso) return '';

  let d: Date;
  const dateOnlyMatch = /^(\d{4})-(\d{2})-(\d{2})$/.exec(iso);
  if (dateOnlyMatch) {
    const year = Number(dateOnlyMatch[1]);
    const month = Number(dateOnlyMatch[2]);
    const day = Number(dateOnlyMatch[3]);
    d = new Date(year, month - 1, day);
  } else {
    const normalized = iso.includes('T') ? iso : iso.replace(' ', 'T');
    d = new Date(normalized);
  }

  if (Number.isNaN(d.getTime())) return iso;
  if (compact) {
    return d.toLocaleDateString(undefined, { month: 'short', year: '2-digit' });
  }
  return d.toLocaleDateString(undefined, { month: 'short', day: 'numeric', year: 'numeric' });
}

function isDocumentDarkMode(): boolean {
  if (typeof document === 'undefined') return false;
  return document.documentElement.classList.contains('dark-mode');
}

function useDarkModeClass(): boolean {
  const [dark, setDark] = React.useState(isDocumentDarkMode);

  React.useEffect(() => {
    const sync = () => setDark(isDocumentDarkMode());
    sync();
    document.addEventListener('turbolinks:load', sync);
    window.addEventListener('pageshow', sync);
    document.addEventListener('themechange', sync);
    return () => {
      document.removeEventListener('turbolinks:load', sync);
      window.removeEventListener('pageshow', sync);
      document.removeEventListener('themechange', sync);
    };
  }, []);

  return dark;
}

function PricePerformanceChart({ ticker, variant = 'inline', fullPageHref }: Props) {
  const isDark = useDarkModeClass();
  const [timeframe, setTimeframe] = React.useState<Timeframe>('1Y');
  const [basis, setBasis] = React.useState<Basis>('price');
  const [data, setData] = React.useState<PerformanceResponse | null>(null);
  const [loading, setLoading] = React.useState<boolean>(false);
  const [error, setError] = React.useState<string | null>(null);
  const [containerWidth, setContainerWidth] = React.useState<number>(0);
  const [reloadCounter, setReloadCounter] = React.useState<number>(0);

  const canvasRef = React.useRef<HTMLCanvasElement | null>(null);
  const chartRef = React.useRef<Chart | null>(null);
  const wrapperRef = React.useRef<HTMLDivElement | null>(null);
  const cacheRef = React.useRef<Map<string, PerformanceResponse>>(new Map());
  const abortRef = React.useRef<AbortController | null>(null);

  // Key must include ticker so that if a parent ever reuses this component
  // across different tickers (e.g. a comparison view, a client-side router),
  // we don't serve the previous ticker's series for the same (timeframe,
  // basis) combination. The fetch URL already varies by ticker, so this
  // keeps the memo cache consistent with the network surface.
  const cacheKey = `${ticker}:${timeframe}:${basis}`;

  React.useEffect(() => {
    const cached = cacheRef.current.get(cacheKey);
    if (cached) {
      setData(cached);
      setError(null);
      setLoading(false);
      return;
    }

    if (abortRef.current) {
      abortRef.current.abort();
    }
    const controller = new AbortController();
    abortRef.current = controller;

    setLoading(true);
    setError(null);

    const url = `/ticker/${encodeURIComponent(ticker)}/price_performance/data?timeframe=${encodeURIComponent(timeframe)}&basis=${encodeURIComponent(basis)}`;

    fetch(url, { credentials: 'same-origin', signal: controller.signal })
      .then(async (response) => {
        if (!response.ok) {
          throw new Error(`Request failed: ${response.status}`);
        }
        const json = (await response.json()) as PerformanceResponse;
        return json;
      })
      .then((json) => {
        cacheRef.current.set(cacheKey, json);
        setData(json);
        setLoading(false);
      })
      .catch((err) => {
        if (err && err.name === 'AbortError') return;
        setError('We could not load price performance. Please try again.');
        setLoading(false);
      });

    return () => {
      controller.abort();
    };
  }, [ticker, timeframe, basis, cacheKey, reloadCounter]);

  React.useEffect(() => {
    const el = wrapperRef.current;
    if (!el || typeof ResizeObserver === 'undefined') return;
    const observer = new ResizeObserver((entries) => {
      const width = entries[0]?.contentRect?.width ?? 0;
      setContainerWidth(width);
    });
    observer.observe(el);
    setContainerWidth(el.clientWidth);
    return () => observer.disconnect();
  }, []);

  React.useEffect(() => {
    if (!canvasRef.current || !data || data.series.length === 0) {
      if (chartRef.current) {
        chartRef.current.destroy();
        chartRef.current = null;
      }
      return;
    }

    const valueKey: keyof SeriesPoint = basis === 'total_return' ? 'adj_close' : 'close';
    const points = data.series
      .map((row) => ({ x: row.t, y: row[valueKey] as number | null }))
      .filter((p) => p.y !== null) as { x: string; y: number }[];

    if (points.length === 0) {
      if (chartRef.current) {
        chartRef.current.destroy();
        chartRef.current = null;
      }
      return;
    }

    const isPositive =
      data.summary.change_percent !== null && data.summary.change_percent !== undefined
        ? data.summary.change_percent >= 0
        : points[points.length - 1].y >= points[0].y;

    const lineColor = isPositive ? '#2e7d32' : '#c62828';
    const fillColor = isPositive ? 'rgba(46, 125, 50, 0.12)' : 'rgba(198, 40, 40, 0.12)';
    const latestValue = points[points.length - 1].y;
    const compactTicks = containerWidth > 0 && containerWidth < 480;
    const showLegend = variant === 'full' && !(containerWidth > 0 && containerWidth < 480);

    if (chartRef.current) {
      chartRef.current.destroy();
      chartRef.current = null;
    }

    const ctx = canvasRef.current.getContext('2d');
    if (!ctx) return;

    chartRef.current = new Chart(ctx, {
      type: 'line',
      data: {
        labels: points.map((p) => p.x),
        datasets: [
          {
            label: basis === 'total_return' ? 'Total return' : ticker.toUpperCase(),
            data: points.map((p) => p.y),
            borderColor: lineColor,
            backgroundColor: fillColor,
            borderWidth: 2,
            pointRadius: 0,
            pointHoverRadius: 4,
            pointHoverBackgroundColor: lineColor,
            fill: true,
            tension: 0.15,
            spanGaps: true
          }
        ]
      },
      options: {
        responsive: true,
        maintainAspectRatio: false,
        resizeDelay: 100,
        interaction: {
          mode: 'index',
          intersect: false
        },
        plugins: {
          legend: {
            display: showLegend
          },
          tooltip: {
            enabled: true,
            displayColors: false,
            callbacks: {
              title: (items) => {
                const first = items[0];
                if (!first) return '';
                return formatDateLabel(String(first.label ?? first.parsed.x), false);
              },
              label: (item) => {
                const value = Number(item.parsed.y);
                const perfLabel = basis === 'total_return' ? 'Total return from this date' : 'Price performance to now';
                const lines = [
                  `${basis === 'total_return' ? 'Adjusted close' : 'Close'}: ${formatCurrency(value)}`
                ];
                if (latestValue && value) {
                  const pct = ((latestValue - value) / value) * 100;
                  lines.push(`${perfLabel}: ${formatPercent(pct)}`);
                }
                return lines;
              }
            }
          }
        },
        scales: {
          x: {
            ticks: {
              autoSkip: true,
              maxRotation: 0,
              maxTicksLimit: compactTicks ? 4 : 8,
              callback: (value, index) => {
                const raw = points[index]?.x ?? '';
                return formatDateLabel(raw, compactTicks);
              }
            },
            grid: {
              display: false
            }
          },
          y: {
            ticks: {
              maxTicksLimit: compactTicks ? 4 : 6,
              callback: (value) => formatCurrency(Number(value))
            },
            grid: {
              color: 'rgba(0, 0, 0, 0.06)'
            }
          }
        }
      }
    });
  }, [data, basis, ticker, containerWidth, variant]);

  React.useEffect(() => {
    return () => {
      if (chartRef.current) {
        chartRef.current.destroy();
        chartRef.current = null;
      }
      if (abortRef.current) {
        abortRef.current.abort();
      }
    };
  }, []);

  const summary = data?.summary;
  const isPositive =
    summary && summary.change_percent !== null && summary.change_percent !== undefined
      ? summary.change_percent >= 0
      : true;
  const summaryColor = isPositive ? '#2e7d32' : '#c62828';

  const isFull = variant === 'full';
  const chartWrapperStyle: React.CSSProperties = isFull
    ? { position: 'relative', width: '100%', aspectRatio: '16 / 9', minHeight: 360 }
    : { position: 'relative', width: '100%', height: 180 };

  const friendly =
    summary && summary.change_percent !== null && summary.change_percent !== undefined
      ? `${ticker.toUpperCase()} is ${summary.change_percent >= 0 ? 'up' : 'down'} ${formatPercent(summary.change_percent)} ${TIMEFRAME_COPY[timeframe]}.`
      : '';

  const dividendToggleId = React.useMemo(
    () => `pp-dividends-${Math.random().toString(36).slice(2, 10)}`,
    []
  );

  const headerMarginBottom = isFull ? 12 : 8;
  const controlsMarginBottom = isFull ? 12 : 8;
  const footerMarginTop = isFull ? 12 : 8;
  const titleFontSize = isFull ? undefined : '1rem';
  const descriptionFontSize = isFull ? '0.875rem' : '0.8125rem';

  return (
    <div className="card">
      <div className="card-body">
        <div
          style={{
            display: 'flex',
            flexWrap: 'wrap',
            alignItems: 'flex-start',
            justifyContent: 'space-between',
            gap: 12,
            marginBottom: headerMarginBottom
          }}
        >
          <div style={{ minWidth: 0, flex: '1 1 260px' }}>
            <h4
              className="card-title"
              style={{ marginBottom: 2, fontSize: titleFontSize }}
            >
              Price Performance
            </h4>
            <p
              className="text-muted"
              style={{ marginBottom: 0, fontSize: descriptionFontSize }}
            >
              {isFull
                ? `See how ${ticker.toUpperCase()} has performed. Hover the chart to see the return from that date to today.`
                : `Hover to see ${ticker.toUpperCase()}'s return from that date to today.`}
            </p>
          </div>
          {summary && summary.change_percent !== null && summary.change_percent !== undefined && (
            <div style={{ textAlign: 'right', whiteSpace: 'nowrap' }} aria-live="polite">
              <div
                style={{
                  color: summaryColor,
                  fontWeight: 600,
                  fontSize: '1.25rem',
                  lineHeight: 1.2
                }}
              >
                {formatPercent(summary.change_percent)}
              </div>
              <div className="text-muted" style={{ fontSize: '0.75rem' }}>
                {formatCurrency(summary.start_price)} &rarr; {formatCurrency(summary.end_price)}
              </div>
            </div>
          )}
        </div>

        <div
          style={{
            display: 'flex',
            flexWrap: 'wrap',
            gap: 12,
            alignItems: 'center',
            justifyContent: 'space-between',
            marginBottom: controlsMarginBottom
          }}
        >
          <div
            style={{
              overflowX: 'auto',
              WebkitOverflowScrolling: 'touch',
              maxWidth: '100%'
            }}
          >
            <div
              role="group"
              aria-label="Select timeframe"
              style={{
                display: 'inline-flex',
                alignItems: 'stretch',
                border: isDark ? '1px solid rgba(255, 255, 255, 0.12)' : '1px solid rgba(0, 0, 0, 0.15)',
                borderRadius: 10,
                overflow: 'hidden',
                background: isDark ? 'rgba(255, 255, 255, 0.02)' : 'rgba(0, 0, 0, 0.03)'
              }}
            >
              {TIMEFRAMES.map((tf, idx) => {
                const active = tf === timeframe;
                const dividerColor = isDark ? 'rgba(255, 255, 255, 0.08)' : 'rgba(0, 0, 0, 0.1)';
                const activeBg = isDark ? 'rgba(255, 255, 255, 0.12)' : 'rgba(0, 0, 0, 0.08)';
                const inactiveColor = isDark ? 'rgba(255, 255, 255, 0.72)' : 'rgba(0, 0, 0, 0.65)';
                const activeColor = isDark ? '#ffffff' : 'rgba(0, 0, 0, 0.9)';
                const hoverBg = isDark ? 'rgba(255, 255, 255, 0.06)' : 'rgba(0, 0, 0, 0.06)';
                const hoverColor = isDark ? '#ffffff' : 'rgba(0, 0, 0, 0.9)';
                return (
                  <button
                    key={tf}
                    type="button"
                    onClick={() => setTimeframe(tf)}
                    aria-pressed={active}
                    style={{
                      appearance: 'none',
                      WebkitAppearance: 'none',
                      border: 'none',
                      outline: 'none',
                      cursor: 'pointer',
                      padding: '6px 14px',
                      fontSize: '0.875rem',
                      fontWeight: active ? 600 : 500,
                      lineHeight: 1.4,
                      background: active ? activeBg : 'transparent',
                      color: active ? activeColor : inactiveColor,
                      borderLeft: idx === 0 ? 'none' : `1px solid ${dividerColor}`,
                      transition: 'background-color 120ms ease, color 120ms ease',
                      whiteSpace: 'nowrap'
                    }}
                    onMouseEnter={(e) => {
                      if (!active) {
                        e.currentTarget.style.color = hoverColor;
                        e.currentTarget.style.background = hoverBg;
                      }
                    }}
                    onMouseLeave={(e) => {
                      if (!active) {
                        e.currentTarget.style.color = inactiveColor;
                        e.currentTarget.style.background = 'transparent';
                      }
                    }}
                  >
                    {tf}
                  </button>
                );
              })}
            </div>
          </div>

          <label
            htmlFor={dividendToggleId}
            style={{
              display: 'inline-flex',
              alignItems: 'center',
              gap: 6,
              marginBottom: 0,
              cursor: 'pointer',
              fontSize: '0.875rem'
            }}
            title="When on, the chart plots total return (dividend reinvestment) using split- and dividend-adjusted prices."
          >
            <input
              id={dividendToggleId}
              type="checkbox"
              checked={basis === 'total_return'}
              onChange={(e) => setBasis(e.target.checked ? 'total_return' : 'price')}
              style={{ margin: 0 }}
            />
            <span>Include dividends</span>
          </label>
        </div>

        <div ref={wrapperRef} style={chartWrapperStyle}>
          {loading && (
            <div
              style={{
                position: 'absolute',
                inset: 0,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                zIndex: 1
              }}
            >
              <div className="spinner-border text-primary" role="status" style={{ width: 32, height: 32 }}>
                <span className="sr-only">Loading...</span>
              </div>
            </div>
          )}
          {error && !loading && (
            <div
              style={{
                position: 'absolute',
                inset: 0,
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                justifyContent: 'center',
                zIndex: 1,
                textAlign: 'center',
                padding: 16
              }}
            >
              <div className="text-danger" style={{ fontSize: '0.875rem' }}>
                {error}
              </div>
              <button
                type="button"
                className="btn btn-sm btn-outline-primary"
                style={{ marginTop: 8 }}
                onClick={() => {
                  cacheRef.current.delete(cacheKey);
                  setReloadCounter((n) => n + 1);
                }}
              >
                Retry
              </button>
            </div>
          )}
          {!error && data && data.series.length === 0 && !loading && (
            <div
              style={{
                position: 'absolute',
                inset: 0,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center'
              }}
            >
              <div className="text-muted" style={{ fontSize: '0.875rem' }}>
                No price data available for this timeframe.
              </div>
            </div>
          )}
          <canvas ref={canvasRef} style={{ display: 'block', width: '100%', height: '100%' }} />
        </div>

        <div
          style={{
            display: 'flex',
            flexWrap: 'wrap',
            gap: 8,
            alignItems: 'center',
            justifyContent: 'space-between',
            marginTop: footerMarginTop
          }}
        >
          {friendly ? (
            <div
              className="text-muted"
              aria-live="polite"
              style={{ fontSize: '0.875rem', flex: '1 1 auto', minWidth: 0 }}
            >
              {friendly}
            </div>
          ) : (
            <span />
          )}
          {fullPageHref && (
            <a
              href={fullPageHref}
              className="btn btn-sm btn-outline-primary"
              style={{ whiteSpace: 'nowrap' }}
            >
              View full price performance analysis
            </a>
          )}
        </div>
      </div>
    </div>
  );
}

export default PricePerformanceChart;
